/**
 * Dropdown Menu Handler
 * Handles click events, actions delegation and controller integration
 */

import { dropdownListener } from "./dropdown-listener";
import { dropdownToggle } from "./dropdown-toggle";
import { dropdownForm } from "./dropdown-form";

type DropdownMenuType = "post" | "comment";

class DropdownHandler {
    public isInitialized: boolean;

    public constructor() {
        this.isInitialized = false;
    }

    public initialize(): void {
        if (this.isInitialized) {
            this.setDropdownControlsInteractive(document, true);
            return;
        }

        dropdownListener.initialize(this);

        this.isInitialized = true;
        this.setDropdownControlsInteractive(document, true);
    }

    public setDropdownControlsInteractive(root: ParentNode = document, enabled = true): void {
        if (!root) {
            return;
        }

        dropdownForm.getDropdownControls(root).forEach((el) => {
            el.disabled = !enabled;
            el.setAttribute("aria-disabled", enabled ? "false" : "true");
            if (enabled) {
                el.removeAttribute("data-dropdown-disabled");
            } else {
                el.setAttribute("data-dropdown-disabled", "1");
            }
        });
    }

    public handleMenuButtonClick(event: Event, type: DropdownMenuType): void {
        event.preventDefault();
        event.stopPropagation();

        const itemIdAttr = type === "post" ? "data-post-id" : "data-comment-id";
        const target = dropdownForm.getEventTarget(event);
        const button = dropdownForm.getMenuButton(target, type);
        if (button instanceof HTMLButtonElement && button.disabled) {
            return;
        }
        const rawItemId = button?.getAttribute(itemIdAttr);
        const itemId = rawItemId ? Number(rawItemId) : NaN;
        const isInModal = !!dropdownForm.getModalBody(button);

        if (Number.isInteger(itemId) && itemId > 0 && dropdownToggle) {
            dropdownToggle.toggleDropdownMenu(type, itemId, isInModal);
        }
    }

    public handleMenuItemClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (dropdownToggle) {
            dropdownToggle.hideAllDropdownMenus();
        }
    }
}

const dropdownHandler = new DropdownHandler();

export const initializeDropdownMenus = (): void => dropdownHandler.initialize();
export const setDropdownControlsInteractive = (root: ParentNode = document, enabled = true): void =>
    dropdownHandler.setDropdownControlsInteractive(root, enabled);

export { dropdownHandler, DropdownHandler };
